import type { WmtAppPlatform } from './constants'

const WMT_APP_EXTERNAL_DEEPLINK_BASE = 'walmart://healthandwellness/bettercareservices'

/**
 * Hostnames that stay inside the webview (not rewritten to a native deeplink).
 * Exact match or any subdomain of an entry counts as internal.
 */
export const INTERNAL_HREF_DOMAINS = [
    'bettercareservices.walmart.com',
    'qa-bettercareservices.walmart.com',
    'generalmedicine.co',
    'staging-nile.com',
] as const

function isInternalHostname(hostname: string): boolean {
    const normalizedHostname = hostname.toLowerCase()
    return INTERNAL_HREF_DOMAINS.some((domain) => {
        const normalizedDomain = domain.toLowerCase()
        return (
            normalizedHostname === normalizedDomain ||
            normalizedHostname.endsWith(`.${normalizedDomain}`)
        )
    })
}

export type ResolveExternalHrefOptions = {
    /** Parsed `wmt-app-platform` cookie value, or null when absent/invalid. */
    platform: WmtAppPlatform | null
    /** From `tenant.capabilities.nativeAppExternalDeeplinks`. */
    nativeAppExternalDeeplinks: boolean
    /**
     * Storefront origin used to classify cross-origin links and to absolutize
     * relative hrefs when `openExternally` is true (e.g. `https://host`).
     */
    storefrontOrigin?: string
    /**
     * Treat the href as leaving the webview even when same-origin
     * (`target="_blank"` / `window.open`).
     */
    openExternally?: boolean
}

/** Build the Walmart native deeplink that opens `externalUrl` in the device browser. */
export function buildWmtAppDeeplink(externalUrl: string): string {
    return `${WMT_APP_EXTERNAL_DEEPLINK_BASE}?provider=${encodeURIComponent(externalUrl)}`
}

/**
 * Whether `href` is an absolute http(s) URL whose origin differs from the storefront.
 * Relative paths, non-http(s) schemes, and `INTERNAL_HREF_DOMAINS` (including subdomains)
 * return false.
 */
export function isExternalHref(href: string, storefrontOrigin?: string): boolean {
    let parsed: URL
    try {
        parsed = new URL(href)
    } catch {
        return false
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return false
    }

    if (isInternalHostname(parsed.hostname)) {
        return false
    }

    if (!storefrontOrigin) {
        return true
    }

    let storefront: URL
    try {
        storefront = new URL(storefrontOrigin)
    } catch {
        return true
    }

    return parsed.origin !== storefront.origin
}

function resolveAbsoluteHref(href: string, storefrontOrigin?: string): string | null {
    try {
        return new URL(href).toString()
    } catch {
        // relative or invalid
    }

    if (!storefrontOrigin) return null

    try {
        return new URL(href, storefrontOrigin).toString()
    } catch {
        return null
    }
}

/**
 * When the Walmart native-app capability is on and a platform cookie is present,
 * rewrite external (or explicitly externalized) hrefs to the WMT deeplink.
 * Otherwise return `href` unchanged.
 */
export function resolveExternalHref(href: string, options: ResolveExternalHrefOptions): string {
    const { platform, nativeAppExternalDeeplinks, storefrontOrigin, openExternally = false } = options

    if (!nativeAppExternalDeeplinks || !platform) {
        return href
    }

    const absoluteHref = resolveAbsoluteHref(href, storefrontOrigin)
    if (!absoluteHref) {
        return href
    }

    // Already a Walmart native deeplink — do not wrap again.
    if (href.startsWith(`${WMT_APP_EXTERNAL_DEEPLINK_BASE}?`) || href === WMT_APP_EXTERNAL_DEEPLINK_BASE) {
        return href
    }

    // openExternally skips the same-origin check only; non-http(s) schemes never wrap.
    if (!isExternalHref(absoluteHref, openExternally ? undefined : storefrontOrigin)) {
        return href
    }

    return buildWmtAppDeeplink(absoluteHref)
}
